/**
 * Fitting words into a box (SRS C4).
 *
 * Canvas has no text wrapping of its own: it draws one line where it is told and
 * nothing else. So the words are broken into lines here, once, and the painter draws
 * the lines it is given.
 *
 * How wide a piece of text is depends on the font, which only the canvas knows, so
 * measuring is passed in. That keeps this file pure — it can be tested with a ruler
 * that says every character is one unit wide — and it keeps the same wrapping in
 * front of the teacher as the one used to work out how tall the box has to be.
 *
 * Everything here is in CANVAS units, never screen pixels, so the words wrap the same
 * way at every zoom (SRS C3).
 */

/** From the type scale: 1rem of text on a 1.5rem line (docs/07-DESIGN-SYSTEM.md §3). */
export const LINE_HEIGHT_RATIO = 1.5;

/** How wide a piece of text is, in canvas units. */
export type MeasureText = (text: string) => number;

/** The height of one line of text at this size, in canvas units. */
export function lineHeight(fontSize: number): number {
  return fontSize * LINE_HEIGHT_RATIO;
}

/** How tall a box has to be to hold this many lines. */
export function textHeight(lineCount: number, fontSize: number): number {
  return Math.max(lineCount, 1) * lineHeight(fontSize);
}

/**
 * A word too long for the box, cut into pieces that fit.
 *
 * A long web address or a run of digits has nowhere to break, and leaving it to spill
 * out of the box would put it under whatever is beside it. It is cut instead.
 */
function breakWord(word: string, maxWidth: number, measure: MeasureText): string[] {
  const pieces: string[] = [];
  let piece = '';
  for (const character of word) {
    const next = piece + character;
    if (piece !== '' && measure(next) > maxWidth) {
      pieces.push(piece);
      piece = character;
      continue;
    }
    piece = next;
  }
  if (piece !== '') pieces.push(piece);
  return pieces;
}

/** One paragraph — text with no line break of its own — as the lines it fills. */
function wrapParagraph(
  paragraph: string,
  maxWidth: number,
  measure: MeasureText,
): string[] {
  const words = paragraph.split(' ');
  const lines: string[] = [];
  let line = '';

  for (const word of words) {
    const candidate = line === '' ? word : `${line} ${word}`;
    if (line !== '' && measure(candidate) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
    // Whatever is on the line now may still be a single word too wide to fit.
    if (measure(line) <= maxWidth || line.includes(' ')) continue;
    const pieces = breakWord(line, maxWidth, measure);
    line = pieces.pop() ?? '';
    lines.push(...pieces);
  }

  lines.push(line);
  return lines;
}

/**
 * The lines the text fills, at this width.
 *
 * A line break the teacher typed is always kept: an empty line between two paragraphs
 * is deliberate and stays empty. Everything else wraps on spaces.
 */
export function wrapLines(
  content: string,
  maxWidth: number,
  measure: MeasureText,
): string[] {
  if (maxWidth <= 0) return content === '' ? [''] : content.split('\n');
  const lines: string[] = [];
  for (const paragraph of content.split('\n')) {
    lines.push(...wrapParagraph(paragraph, maxWidth, measure));
  }
  return lines;
}

/**
 * A rough ruler for when there is no canvas to measure with.
 *
 * Used by tests and by the moment before the first paint. Half the font size per
 * character is close enough for a proportional face to keep a box from being wildly
 * the wrong height; the real measurement replaces it as soon as anything is drawn.
 */
export function estimateWidth(text: string, fontSize: number): number {
  return text.length * fontSize * 0.5;
}
